/**
 * Sets the document title and meta description for a route.
 *
 * Imperative rather than React 19's hoisted `<title>` element, for one reason: two components
 * rendering a `<title>` at once — a route and a nested panel, say — leaves which one wins up to
 * render order. A page has exactly one title, and this makes that structural.
 *
 * It also feeds the route announcer (see `lib/page-title.ts`), so a screen-reader user is told
 * where they have arrived. That is the whole reason the title has to be set by the component that
 * knows it rather than guessed by the shell.
 */
import { useEffect } from 'react';

import { setPageTitle } from '../lib/page-title.js';

export const SITE_NAME = 'HCLTech course catalog';

export interface PageMeta {
  /** The page's own title, without the site name. Pass `null` while it is still loading. */
  readonly title: string | null;
  readonly description?: string;
}

export function usePageMeta({ title, description }: PageMeta): void {
  useEffect(() => {
    // Nothing is announced and nothing is written while the title is unknown. An intermediate
    // "Loading…" in the tab and in the live region is noise, not information.
    if (title === null) return;

    const full = title === SITE_NAME ? SITE_NAME : `${title} — ${SITE_NAME}`;
    document.title = full;
    setPageTitle(title);

    if (description === undefined) return;

    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (meta === null) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.append(meta);
    }
    meta.content = description;
  }, [title, description]);
}
